<script setup lang="ts">
import type { GuestbookMessage } from "@/models/message"
import type { IMenuItem } from "@/types"
import { computed, ref } from "vue"
import { Button, DropdownMenu, Icon, ReadMore } from "@/components/ui"
import { useTranslation } from "@/composables"
import { GuestbookMessageType } from "@/models/message"

interface GuestbookMessageCardProps {
  message?: GuestbookMessage
  isLoading?: boolean
  canDelete?: boolean
  canEdit?: boolean
}

const props = withDefaults(defineProps<GuestbookMessageCardProps>(), { message: undefined, isLoading: false, canDelete: false, canEdit: false })

const emit = defineEmits<{
  (e: "delete", message: GuestbookMessage): void
  (e: "edit", message: GuestbookMessage): void
  (e: "flag", message: GuestbookMessage): void
}>()

const { t } = useTranslation()

const actionsMenuOpen = ref(false)

const fromInitials = computed(() => {
  const name = props.message?.fromName?.trim()
  if (!name)
    return "?"

  const parts = name.split(/\s+/)

  if (parts.length > 1) {
    return `${parts[0][0]}${parts.at(-1)![0]}`.toUpperCase()
  }

  return name.slice(0, 2).toUpperCase()
})

const messageMenuItems = computed((): IMenuItem[] => {
  const items: IMenuItem[] = []
  if (props.canEdit) {
    items.push({
      key: "edit-message",
      label: t("guestbook_message_card.edit_message"),
      icon: "Edit",
      onClick: () => props.message?.id && emit("edit", props.message),
      dismissOnInteract: true,
    })
  }
  if (props.canDelete) {
    items.push({
      key: "delete-message",
      label: t("guestbook_message_card.delete_message"),
      icon: "Trash",
      variant: "danger",
      onClick: () => props.message?.id && emit("delete", props.message),
      dismissOnInteract: true,
    })
  }
  items.push({
    key: "flag-message",
    label: t("guestbook_message_card.flag_message"),
    icon: "Flag",
    onClick: () => props.message?.id && emit("flag", props.message),
    dismissOnInteract: true,
  })
  return items
})
</script>

<template>
  <div
    v-if="isLoading"
    class="px-0 py-0.5 sm:p-1"
    data-test="message-card-loading"
  >
    <div class="relative flex min-h-40 flex-col rounded-2xl border border-neutral-300/80 bg-neutral-100 sm:min-h-48 sm:rounded-[1.35rem]">
      <div class="relative flex-1 px-3 pb-2.5 pt-3 sm:px-3.5 sm:pb-3 sm:pt-4">
        <div class="mb-2 h-3 w-[72%] animate-pulse rounded bg-neutral-200/90" />
        <div class="h-3 w-[55%] animate-pulse rounded bg-neutral-200/90" />
      </div>
      <div class="flex items-center gap-2 border-t border-neutral-200/70 px-3 py-2.5 sm:gap-3 sm:px-3.5 sm:py-3">
        <div class="size-9 shrink-0 animate-pulse rounded-full bg-neutral-200/90 sm:size-10" />
        <div class="min-w-0 flex-1 space-y-1.5">
          <div class="h-2.5 w-10 animate-pulse rounded bg-neutral-200/90" />
          <div class="h-4 max-w-40 animate-pulse rounded bg-neutral-200/90" />
        </div>
      </div>
      <div class="absolute bottom-0 left-7 h-3 w-3 translate-y-1/2 rotate-45 border border-neutral-300/80 border-t-0 border-l-0 bg-neutral-100 sm:left-8" />
    </div>
  </div>
  <article
    v-else-if="message"
    class="guest-layout group px-0 py-0.5 sm:p-1"
    data-test="message-card"
  >
    <div class="relative flex h-full min-h-0 flex-col rounded-2xl border border-neutral-300/85 bg-linear-to-br from-white to-neutral-100/90 shadow-sm shadow-neutral-900/5 ring-1 ring-neutral-950/5 sm:rounded-[1.35rem]">
      <div class="absolute bottom-0 left-7 h-3 w-3 translate-y-1/2 rotate-45 border border-neutral-300/85 border-t-0 border-l-0 bg-neutral-100 sm:left-8" />
      <div class="relative z-1 min-h-0 min-w-0 flex-1 px-3 pb-2.5 pt-3 text-sm leading-relaxed text-neutral-800 sm:px-3.5 sm:pb-3 sm:pt-4 sm:text-[15px]">
        <div
          v-if="message.type === GuestbookMessageType.Text"
          class="flex min-w-0 items-start gap-1 sm:gap-1.5 md:gap-2"
        >
          <span class="shrink-0 select-none font-serif text-[1.75rem] leading-[1.05] text-neutral-400/45 sm:text-[2.25rem] md:text-[2.5rem]">
            &ldquo;
          </span>
          <div v-if="message.message" class="min-w-0 flex-1 pt-0.5">
            <ReadMore :content="message.message" :lines="10" />
          </div>
        </div>
        <div
          v-else-if="message.type === GuestbookMessageType.Video && message.videoUrl"
          class="overflow-hidden rounded-lg bg-neutral-950/4 ring-1 ring-neutral-900/5 sm:rounded-xl"
        >
          <video controls class="max-h-[min(42vh,14rem)] w-full object-contain sm:max-h-[min(50vh,18rem)]" :poster="message.snapshotUrl">
            <source :src="message.videoUrl" type="video/mp4">
          </video>
        </div>
      </div>

      <footer class="relative z-1 flex shrink-0 items-center justify-between gap-1.5 border-t border-neutral-200/80 px-3 py-2 sm:gap-2 sm:px-3.5 sm:pb-3 sm:pt-2.5">
        <div class="flex min-w-0 items-center gap-2 sm:gap-3">
          <div class="flex size-9 shrink-0 items-center justify-center rounded-full bg-[color-mix(in_srgb,var(--template-primary-colour),white_64%)] text-[0.6875rem] font-semibold tracking-wide text-[color-mix(in_srgb,var(--template-primary-colour),black_18%)] sm:size-10 sm:text-xs">
            {{ fromInitials }}
          </div>
          <div class="min-w-0">
            <p class="text-xs font-medium uppercase tracking-wide text-neutral-500 sm:tracking-wider">
              {{ t("guestbook_message_card.from_label") }}
            </p>
            <p class="truncate text-xs font-semibold text-neutral-900 sm:text-sm">
              {{ message.fromName }}
            </p>
          </div>
        </div>
        <div
          class="shrink-0 transition-opacity duration-200"
          :class="actionsMenuOpen ? 'opacity-100' : 'opacity-100 md:opacity-0 md:group-hover:opacity-100 md:group-focus-within:opacity-100'"
        >
          <DropdownMenu
            v-model:open="actionsMenuOpen"
            :items="messageMenuItems"
            side="top"
            align="end"
          >
            <template #activator>
              <Button variant="icon" size="sm">
                <Icon icon="MenuEllipsisVertical" size="sm" />
              </Button>
            </template>
          </DropdownMenu>
        </div>
      </footer>
    </div>
  </article>
</template>
